/**
 * Semantik analiz renkleri — her yeni analiz bu 5 kategoriden birine atanır.
 * Backend'den gelen rastgele hex `color` alanları render kaynağı DEĞİLDİR;
 * ölçü / ordinal üzerinden sequential expression kullanılır.
 */

export const ANALYSIS_SEMANTIC = Object.freeze({
  accessibility: 'accessibility',
  coverage: 'coverage',
  problem: 'problem',
  equity: 'equity',
  recommendation: 'recommendation',
})

/** Owner → semantik kategori */
export const OWNER_SEMANTIC = Object.freeze({
  isochrone: ANALYSIS_SEMANTIC.accessibility,
  catchment: ANALYSIS_SEMANTIC.accessibility,
  'service-availability': ANALYSIS_SEMANTIC.coverage,
  'transit-desert': ANALYSIS_SEMANTIC.problem,
  corridor: ANALYSIS_SEMANTIC.problem,
})

/** Accessibility — mor sequential (ColorBrewer Purples) */
export const ACCESSIBILITY_PURPLES = Object.freeze([
  '#f3e8ff',
  '#e9d5ff',
  '#d8b4fe',
  '#c084fc',
  '#a855f7',
  '#9333ea',
  '#7e22ce',
  '#6b21a8',
])

/** Coverage — mavi sequential (ColorBrewer Blues) */
export const COVERAGE_BLUES = Object.freeze([
  '#eff6ff',
  '#dbeafe',
  '#bfdbfe',
  '#93c5fd',
  '#60a5fa',
  '#3b82f6',
  '#2563eb',
  '#1d4ed8',
])

/** Problem — kırmızı yoğunluk */
export const PROBLEM_REDS = Object.freeze([
  '#fef2f2',
  '#fecaca',
  '#fca5a5',
  '#f87171',
  '#ef4444',
  '#dc2626',
  '#b91c1c',
  '#991b1b',
])

/** Equity — OW teal (ileride) */
export const EQUITY_TEALS = Object.freeze([
  '#f0fdfa',
  '#ccfbf1',
  '#99f6e4',
  '#5eead4',
  '#2dd4bf',
  '#14b8a6',
  '#0d9488',
  '#0f766e',
])

/** Recommendation — yeşil (ileride) */
export const RECOMMENDATION_GREENS = Object.freeze([
  '#f0fdf4',
  '#bbf7d0',
  '#86efac',
  '#4ade80',
  '#22c55e',
  '#16a34a',
  '#15803d',
  '#166534',
])

export const SEMANTIC_RAMPS = Object.freeze({
  [ANALYSIS_SEMANTIC.accessibility]: ACCESSIBILITY_PURPLES,
  [ANALYSIS_SEMANTIC.coverage]: COVERAGE_BLUES,
  [ANALYSIS_SEMANTIC.problem]: PROBLEM_REDS,
  [ANALYSIS_SEMANTIC.equity]: EQUITY_TEALS,
  [ANALYSIS_SEMANTIC.recommendation]: RECOMMENDATION_GREENS,
})

/**
 * SA headway kategorileri — sıralı ordinal (0=en iyi … 4=en kötü).
 * Choropleth / buffer: tek renk skalası (mavi); kırmızı yalnız No Service.
 */
export const SA_CATEGORY_ORDINAL = Object.freeze({
  frequent: 0,
  regular: 1,
  limited: 2,
  sparse: 3,
  no_service: 4,
})

/** No Service — tek kırmızı vurgu */
export const SA_NO_SERVICE_RED = '#dc2626'

/**
 * Ordinal 0..3 → açık gri-mavi → koyu mavi; 4 = No Service kırmızı.
 * Çok kategorili gürültü yok — sonuç okunabilirliği için sequential.
 */
export const SA_ORDINAL_TO_BLUE = Object.freeze({
  0: '#1d4ed8', // frequent — koyu mavi
  1: '#3b82f6', // regular
  2: '#93c5fd', // limited — açık mavi
  3: '#dbeafe', // sparse — çok açık
  4: SA_NO_SERVICE_RED, // no_service
})

/** Match expression — category / best_category */
export function saCategoryMatchFillExpr(prop = 'category') {
  return [
    'match',
    ['get', prop],
    'frequent',
    SA_ORDINAL_TO_BLUE[0],
    'regular',
    SA_ORDINAL_TO_BLUE[1],
    'limited',
    SA_ORDINAL_TO_BLUE[2],
    'sparse',
    SA_ORDINAL_TO_BLUE[3],
    'no_service',
    SA_NO_SERVICE_RED,
    'uncovered',
    SA_NO_SERVICE_RED,
    SA_ORDINAL_TO_BLUE[3],
  ]
}

/** SA fill color from ordinal */
export function saSequentialFillExpr() {
  return saCategoryMatchFillExpr('category')
}

/** Koyu tema Base sınır çizgisi */
export const BASE_BOUNDARY_LINE = 'rgba(186, 220, 255, 0.72)'
export const BASE_BOUNDARY_LINE_STRONG = 'rgba(220, 238, 255, 0.95)'
export const BASE_BOUNDARY_FILL = 'rgba(158, 207, 255, 0.05)'
export const INTERACTION_SELECTED_LINE = 'rgba(196, 228, 255, 0.95)'
export const INTERACTION_HOVER_LINE = 'rgba(158, 207, 255, 0.7)'

/**
 * MapLibre interpolate expression — property üzerinde sequential ramp.
 */
export function sequentialInterpolateExpr(prop, min, max, ramp) {
  const stops = []
  const n = ramp.length
  for (let i = 0; i < n; i += 1) {
    const t = n === 1 ? 0 : i / (n - 1)
    const v = min + (max - min) * t
    stops.push(v, ramp[i])
  }
  return [
    'interpolate',
    ['linear'],
    ['coalesce', ['to-number', ['get', prop]], min],
    ...stops,
  ]
}

/** SA category → ordinal match expression */
export function saCategoryOrdinalExpr() {
  return [
    'match',
    ['get', 'category'],
    'frequent',
    0,
    'regular',
    1,
    'limited',
    2,
    'sparse',
    3,
    'no_service',
    4,
    4,
  ]
}

/** Isochrone — profil ayırt edilir, dakika opaklık ile sequential */
export const ISOCHRONE_PROFILE_COLORS = Object.freeze({
  walk: '#2dd4bf', // teal — yürüme
  transit: '#7c3aed', // mor — toplu taşıma
  drive: '#db2777', // magenta — otomobil
})

/** Isochrone fill: profil rengi + dakika opaklığı */
export function isochroneSequentialFillExpr() {
  return [
    'match',
    ['get', 'profile'],
    'walk',
    ISOCHRONE_PROFILE_COLORS.walk,
    'drive',
    ISOCHRONE_PROFILE_COLORS.drive,
    'transit',
    ISOCHRONE_PROFILE_COLORS.transit,
    '#7c3aed',
  ]
}

export function isochroneFillOpacityExpr() {
  return [
    'interpolate',
    ['linear'],
    ['to-number', ['get', 'minutes']],
    5,
    0.5,
    10,
    0.38,
    20,
    0.28,
    30,
    0.2,
    45,
    0.14,
  ]
}

/** Admin coverage choropleth — covered_pct sequential + no-service red */
export function saCoverageChoroplethFillExpr(
  pctProp = 'covered_pct',
  categoryProp = 'best_category'
) {
  return [
    'case',
    [
      'any',
      ['==', ['get', categoryProp], 'no_service'],
      ['==', ['get', categoryProp], 'uncovered'],
      ['<', ['coalesce', ['to-number', ['get', pctProp]], 0], 0.5],
    ],
    SA_NO_SERVICE_RED,
    sequentialInterpolateExpr(pctProp, 0, 100, COVERAGE_BLUES),
  ]
}

/** Catchment / coverage ratio → Accessibility purple */
export function accessibilityCoverageFillExpr(prop = 'covered_pct') {
  return sequentialInterpolateExpr(prop, 0, 100, ACCESSIBILITY_PURPLES)
}

/** Admin coverage service score / covered_pct → Coverage blue */
export function coverageChoroplethFillExpr(prop = 'covered_pct') {
  return sequentialInterpolateExpr(prop, 0, 100, COVERAGE_BLUES)
}

/** Transit desert gap — durak yoksa mavi oasis yok; kırmızı/gri */
export function transitDesertGapFillExpr() {
  return [
    'case',
    ['<=', ['coalesce', ['to-number', ['get', 'stop_count']], 0], 0],
    [
      'case',
      ['==', ['get', 'category'], 'no_service'],
      '#475569',
      PROBLEM_REDS[6],
    ],
    [
      'interpolate',
      ['linear'],
      ['coalesce', ['get', 'gap'], 0],
      -2,
      PROBLEM_REDS[7],
      -0.5,
      PROBLEM_REDS[4],
      0,
      '#1e293b',
      0.5,
      COVERAGE_BLUES[3],
      2,
      COVERAGE_BLUES[6],
    ],
  ]
}

/** Coverage ölçütü: supply_z — duraksız mahalle mavi değil gri/kırmızı */
export function transitDesertSupplyFillExpr() {
  return [
    'case',
    ['<=', ['coalesce', ['to-number', ['get', 'stop_count']], 0], 0],
    [
      'case',
      ['==', ['get', 'category'], 'no_service'],
      '#334155',
      PROBLEM_REDS[5],
    ],
    [
      'interpolate',
      ['linear'],
      ['coalesce', ['get', 'supply_z'], 0],
      -2,
      '#1e293b',
      0,
      COVERAGE_BLUES[2],
      2,
      COVERAGE_BLUES[6],
    ],
  ]
}

export function semanticRampForOwner(ownerId) {
  const key = OWNER_SEMANTIC[ownerId] || ANALYSIS_SEMANTIC.coverage
  return SEMANTIC_RAMPS[key] || COVERAGE_BLUES
}
